// SPDX-License-Identifier: MIT
// Frontend prompt builder for the desktop `claude -p` bridge. A faithful,
// condensed mirror of supabase/functions/magnify/lib.ts (buildSystemPrompt +
// buildUserPrompt) — same output contract + spirit so the desktop fiche matches
// the web one. KEEP IN SYNC with the edge fn (or extract a shared package later).
import type { Locale } from '../i18n/detect';
import type { MagnifyResult } from '../auth/magnify';
import type { StoredMessage } from './conversation-store';

export function buildMagnifyPrompt(idea: string, locale: Locale, attachmentsContext?: string): string {
  const ctx = attachmentsContext && attachmentsContext.trim().length > 0
    ? `\n\n--- Contexte fourni par l'utilisateur (fichiers) ---\n${attachmentsContext}\n--- fin ---\nUtilise ce contexte pour personnaliser la fiche.`
    : '';
  return `Tu es Genesis Magnify, architecte logiciel senior qui transforme une idée d'une ligne en fiche projet structurée qu'un non-dev peut livrer en quelques week-ends avec Claude Code. Réponds dans la langue du flag locale (fr = français, en = anglais).

Principes : concret, opiniâtre, anti-Frankenstein (pas de microservices, pas d'abstraction prématurée, la stack la plus simple qui livre en 1-2 semaines pour un solo non-dev). Varie les stacks selon le besoin (souvent PAS Next.js — privilégie IndexedDB/localStorage pour du single-user, Vite+TS+Supabase pour du form-heavy, Astro pour du contenu, static+webhook pour capturer des leads). Nomme toujours le framework explicitement + justifie en une clause.

Réponds UNIQUEMENT avec un objet JSON valide, sans texte ni fence autour, au format exact :
{"title": string (6-10 mots, déclaratif, sans emoji), "outline": string[] (3-7 user stories « L'utilisateur peut X », pas de plomberie), "first_steps": string[] (3-5 actions concrètes faisables en <1h chacune, pas « bootstrap le repo »), "stack": string (une ligne : framework + couche data + déploiement + justification)}

Locale: ${locale}
Idée : ${idea}${ctx}`;
}

// Desktop converse prompt. The web converse edge fn passes prior turns as
// separate Anthropic messages ; `claude -p` / a single chat-completion call take
// ONE prompt, so we inline the transcript. Mirrors the converse edge fn's
// system + turn prompt + output contract (supabase/functions/converse/lib.ts).
// KEEP IN SYNC with that edge fn.
export function buildConversePrompt(
  history: StoredMessage[],
  message: string,
  locale: Locale,
  manifest: MagnifyResult,
): string {
  const transcript = history.length
    ? history.map((m) => `${m.role === 'user' ? 'User' : 'Genesis'}: ${m.content}`).join('\n')
    : '(no prior turns)';
  return `You are Genesis, a senior software architect having a short, friendly working conversation with a non-developer about THEIR project. A structured project "fiche" already exists. Each turn:
1. Reply conversationally in the user's locale (fr = French, en = English). Warm, concrete, lean. 1-4 sentences. No wall of text.
2. Decide whether the user's message MATERIALLY changes the project (new/dropped feature, scope/stack/step change). ONLY THEN set manifest_changed=true and return the COMPLETE updated fiche in \`manifest\` (full title/outline/first_steps/stack — it replaces the current one, not a diff).
3. For questions, clarifications, encouragement or chit-chat: manifest_changed=false and OMIT manifest. Don't churn versions for non-changes.

When you DO update the manifest, keep Genesis discipline: outline = 3-7 user-story bullets, first_steps = 3-5 concrete <1h actions, stack = one opinionated line, anti-Frankenstein. Preserve untouched parts; fold the new request in. Title stays stable unless the project's nature shifted. You may ask ONE short clarifying question (manifest_changed=false) if ambiguous.

Output JSON only, no preamble, no markdown fences, matching exactly:
{"reply": string, "manifest_changed": boolean, "manifest"?: {"title": string, "outline": string[], "first_steps": string[], "stack": string}}

Locale: ${locale}

--- Current project fiche (the thing we're evolving) ---
Title: ${manifest.title}
Outline:
${manifest.outline.map((o) => `- ${o}`).join('\n')}
First steps:
${manifest.first_steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}
Stack: ${manifest.stack ?? '(none yet)'}
--- End fiche ---

--- Conversation so far ---
${transcript}
--- End conversation ---

User says: ${message}`;
}

// Desktop discover prompt (text path). The web discover-attachments edge fn
// sends multimodal content blocks (images/PDF + text) with a structured-output
// schema ; desktop runs through a single `claude -p` / sovereign prompt over the
// EXTRACTED TEXT of the user's files (multimodal is deferred — ai-lab sequences
// it last). Mirrors the edge fn's buildDiscoverySystemPrompt + output contract
// (supabase/functions/discover-attachments/lib.ts). KEEP IN SYNC with that fn.
export function buildDiscoverPrompt(idea: string, locale: Locale, textContext: string, seen: string[] = []): string {
  const filesLine = seen.length > 0 ? `\nFichiers : ${seen.join(', ')}` : '';
  return `Tu es Genesis Discovery, un architecte senior. L'utilisateur t'a partagé des fichiers pour un projet (ci-dessous, leur contenu texte). Ton boulot AVANT de proposer une solution : comprendre ce que ces documents apportent ET ce que l'utilisateur veut vraiment en faire. Réponds dans la langue du flag locale (fr = français, en = anglais).

En 1 phrase MAX (la "découverte sommaire") : décris ce que tu vois à travers les fichiers, factuel et concret.

Ensuite, pose 3 à 5 questions CIBLÉES qui aident à passer du "j'ai uploadé des trucs" à "voilà précisément ce que je veux que tu construises avec". Évite les questions génériques ("quel est ton objectif ?"), celles qui ré-explicitent l'idée, et les yes/no. Privilégie : un choix entre 2-3 directions tirées du contenu réel, les contraintes implicites, et distinguer référence vs template vs source de données vs inspiration.

Pour chaque question ancrée sur un fichier, ajoute aussi "source" = une courte formule qui se lit après « · » et nomme le fichier réel d'où vient la question (un des noms de « Fichiers » ci-dessous, ex. « d'après notes.md ») ou « d'après ton idée » si elle vient du texte de l'idée — n'invente JAMAIS un nom de fichier ; et "why" = UNE phrase sans jargon disant le manque concret que la question comble. Émets "source" ET "why" ensemble pour une question ancrée, ou aucun des deux pour une clarification générique.

Réponds UNIQUEMENT avec un objet JSON valide, sans texte ni fence autour, au format exact :
{"discovery_summary": string (1 phrase), "questions": [{"id": string ("q1","q2",...), "question": string, "placeholder"?: string, "source"?: string, "why"?: string}]}

Locale: ${locale}
Idée : ${idea}${filesLine}

--- Contenu texte des fichiers ---
${textContext}
--- fin ---`;
}
